// IE Hub - Authentication Service
// Provides abstraction layer for authentication-related API operations

import axios from 'axios';
import { httpGet, httpPost, type HttpRequestConfig } from '../http';
import { authEndpoints, oauthEndpoints } from '../endpoints';
import { femaCapture } from '../../fema/capture';
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  User,
  RequestConfig,
  ServiceResponse,
  SignupRequest,
  SignupResponse,
  MagicLinkRequest,
  MagicLinkResponse,
  CheckUwApplicationResponse,
  VerifyUserRequest,
  VerifyUserResponse,
  GoogleAuthUrlResponse,
  GoogleExchangeRequest,
  GoogleExchangeResponse,
  CreateUserFormRequest,
  CreateUserResponse,
  SignInWithTokenRequest,
  SignInWithTokenResponse,
  ValidateTokenResponse,
} from '../types';

type SignInWithTokenApiPayload = {
  access_token: string;
  refresh_token: string;
  emailAddress: string;
  displayName: string;
  userId: number;
  userName: string;
  forceChangePassword: boolean;
  // API sometimes returns `isTwoStepVerificationRequired` (legacy naming)
  isTwoStepVerificationRequired?: boolean;
  // Some envs may return this newer naming
  twoStepVerificationRequired?: boolean;
  // Partner flag (often "y"/"n" in UAT)
  isPartner?: string;
};

const toHttpConfig = (config?: RequestConfig): HttpRequestConfig => {
  const next: HttpRequestConfig = {};
  if (config?.timeout !== undefined) {
    next.timeout = config.timeout;
  }
  if (config?.headers !== undefined) {
    next.headers = config.headers;
  }
  if (config?.skipAuth !== undefined) {
    next.skipAuth = config.skipAuth;
  }
  return next;
};

/**
 * Authentication Service Class
 * Handles all authentication-related API operations with proper error handling and monitoring
 */
export class AuthService {
  /**
   * Login user with credentials
   */
  static async login(credentials: LoginRequest, config?: RequestConfig): ServiceResponse<LoginResponse> {
    try {
      const response = await httpPost<ApiResponse<LoginResponse>>(
        authEndpoints.login,
        credentials,
        toHttpConfig(config)
      );

      // Store token if login successful
      if (response.data.success && response.data.data?.token) {
        localStorage.setItem('authToken', response.data.data.token);
        if (response.data.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.data.refreshToken);
        }
      }

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_login_error', error, {
          category: 'auth',
          severity: 'warning',
          context: { email: credentials.email },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Register new user
   */
  static async register(userData: RegisterRequest, config?: RequestConfig): ServiceResponse<User> {
    try {
      const response = await httpPost<ApiResponse<User>>(
        authEndpoints.register,
        userData,
        toHttpConfig(config)
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_register_error', error, {
          category: 'auth',
          severity: 'error',
          context: { email: userData.email },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Logout user
   */
  static async logout(config?: RequestConfig): ServiceResponse<void> {
    const accessToken = this.getToken();
    const refreshToken = this.getRefreshToken();

    const attemptLogout = async (bearerToken: string | null): ServiceResponse<void> => {
      const headers: Record<string, string> = { ...(config?.headers ?? {}) };
      if (bearerToken) {
        headers.Authorization = `Bearer ${bearerToken}`;
      }

      const requestConfig: HttpRequestConfig = {
        headers,
        // Prevent the request interceptor from overwriting our Authorization header
        skipAuth: true,
      };
      if (config?.timeout !== undefined) {
        requestConfig.timeout = config.timeout;
      }

      const response = await httpPost<ApiResponse<void>>(authEndpoints.logout, {}, requestConfig);

      return response.data;
    };

    try {
      try {
        // Prefer refresh token first when available (backend commonly revokes sessions via refresh token).
        const primaryToken = refreshToken ?? accessToken;
        const secondaryToken =
          refreshToken !== null && accessToken !== null && refreshToken !== accessToken
            ? accessToken
            : null;

        return await attemptLogout(primaryToken);
      } catch (error) {
        // Fallback: if the primary token is rejected, retry with the alternate token.
        const shouldRetryWithSecondary =
          secondaryToken !== null &&
          axios.isAxiosError(error) &&
          error.response?.status === 401;

        if (shouldRetryWithSecondary) {
          return await attemptLogout(secondaryToken);
        }

        throw error;
      }
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_logout_error', error, {
          category: 'auth',
          severity: 'warning',
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    } finally {
      // Always clear tokens on logout, even if API call fails
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    }
  }

  /**
   * Validate current access token
   * Calls /api/auth/validate-token to check expiry status
   */
  static async validateToken(config?: RequestConfig): ServiceResponse<ValidateTokenResponse> {
    try {
      const response = await httpPost<ApiResponse<ValidateTokenResponse>>(
        authEndpoints.validateToken,
        {},
        toHttpConfig(config)
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_validate_token_error', error, {
          category: 'auth',
          severity: 'warning',
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Refresh authentication token
   * Calls /api/auth/refresh-token with the refresh_token in body
   * Returns new access_token and refresh_token on success
   */
  static async refreshToken(refreshData: RefreshTokenRequest, config?: RequestConfig): Promise<RefreshTokenResponse> {
    try {
      // Use skipAuth to prevent adding expired Bearer token to this request
      const httpConfig = toHttpConfig(config);
      httpConfig.skipAuth = true; // Don't add stale access token automatically
      httpConfig.headers = {
        ...(httpConfig.headers ?? {}),
        // Backend expects refresh token in Authorization header too
        Authorization: `Bearer ${refreshData.refresh_token}`,
      };

      // Backend (UW) reads `req.body.refreshToken` — send camelCase; keep snake_case
      // too so legacy backends still work.
      const response = await httpPost<unknown>(
        authEndpoints.refresh,
        { refreshToken: refreshData.refresh_token, refresh_token: refreshData.refresh_token },
        httpConfig
      );

      // Backend envelope: { success, data: { token, refreshToken, user, expiresAt } }
      // Legacy shape: { access_token, refresh_token, emailAddress, ... } flat.
      const raw = response.data as Record<string, unknown> | undefined;
      const inner = (raw && typeof raw === 'object' && 'data' in raw
        ? (raw as { data: unknown }).data
        : raw) as Record<string, unknown> | undefined;
      const userObj = (inner?.user as Record<string, unknown> | undefined) ?? {};

      const accessToken =
        (inner?.token as string | undefined) ||
        (inner?.access_token as string | undefined) ||
        '';
      const nextRefresh =
        (inner?.refreshToken as string | undefined) ||
        (inner?.refresh_token as string | undefined) ||
        '';

      const tokenData: RefreshTokenResponse = {
        access_token: accessToken,
        refresh_token: nextRefresh,
        emailAddress:
          (userObj.emailAddress as string | undefined) ||
          (userObj.email as string | undefined) ||
          (inner?.emailAddress as string | undefined) ||
          '',
        displayName:
          (userObj.displayName as string | undefined) ||
          (inner?.displayName as string | undefined) ||
          '',
        userId:
          (userObj.userId as number | undefined) ??
          (inner?.userId as number | undefined) ??
          0,
        userName:
          (userObj.userName as string | undefined) ||
          (userObj.email as string | undefined) ||
          (inner?.userName as string | undefined) ||
          '',
        forceChangePassword: Boolean(
          userObj.forceChangePassword ?? inner?.forceChangePassword ?? false
        ),
        twoStepVerificationRequired: Boolean(
          userObj.isTwoStepVerificationRequired ??
            userObj.twoStepVerificationRequired ??
            inner?.twoStepVerificationRequired ??
            false
        ),
      };

      if (tokenData.access_token) {
        localStorage.setItem('authToken', tokenData.access_token);
      }
      if (tokenData.refresh_token) {
        localStorage.setItem('refreshToken', tokenData.refresh_token);
      }

      return tokenData;
    } catch (error) {
      // Clear tokens if refresh fails - user must re-login
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');

      if (!config?.skipErrorCapture) {
        femaCapture('auth_refresh_error', error, {
          category: 'auth',
          severity: 'error',
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Get current user profile
   */
  static async getProfile(config?: RequestConfig): ServiceResponse<User> {
    try {
      const response = await httpGet<ApiResponse<User>>(
        authEndpoints.profile,
        toHttpConfig(config)
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_profile_error', error, {
          category: 'auth',
          severity: 'warning',
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Change user password
   */
  static async changePassword(passwordData: ChangePasswordRequest, config?: RequestConfig): ServiceResponse<void> {
    try {
      const response = await httpPost<ApiResponse<void>>(
        authEndpoints.changePassword,
        passwordData,
        toHttpConfig(config)
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_change_password_error', error, {
          category: 'auth',
          severity: 'error',
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Request password reset
   */
  static async forgotPassword(emailData: ForgotPasswordRequest, config?: RequestConfig): ServiceResponse<void> {
    try {
      const response = await httpPost<ApiResponse<void>>(
        authEndpoints.forgotPassword,
        emailData,
        toHttpConfig(config)
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_forgot_password_error', error, {
          category: 'auth',
          severity: 'warning',
          context: { email: emailData.email },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Reset password with token
   */
  static async resetPassword(resetData: ResetPasswordRequest, config?: RequestConfig): ServiceResponse<void> {
    try {
      const response = await httpPost<ApiResponse<void>>(
        authEndpoints.resetPassword,
        resetData,
        toHttpConfig(config)
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_reset_password_error', error, {
          category: 'auth',
          severity: 'error',
          context: { token: resetData.token },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Check if user is authenticated (has valid token)
   */
  static isAuthenticated(): boolean {
    const token = localStorage.getItem('authToken');
    return !!token;
  }

  /**
   * Get stored authentication token
   */
  static getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Get stored refresh token
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * Clear all authentication data
   */
  static clearAuth(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
  }

  /**
   * Auto-refresh token if needed
   * Returns true if refresh was successful, false if user needs to re-login
   */
  static async autoRefresh(): Promise<boolean> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        return false;
      }

      await this.refreshToken({ refresh_token: refreshToken }, { skipErrorCapture: true });
      return true;
    } catch (error) {
      this.clearAuth();
      return false;
    }
  }

  /**
   * Signup user with email
   */
  static async signup(signupData: SignupRequest, config?: RequestConfig): ServiceResponse<SignupResponse> {
    try {
      const response = await httpPost<ApiResponse<SignupResponse>>(
        authEndpoints.signup,
        signupData,
        toHttpConfig(config)
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_signup_error', error, {
          category: 'auth',
          severity: 'error',
          context: { email: signupData.email },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Request magic link for email
   */
  static async requestMagicLink(magicLinkData: MagicLinkRequest, config?: RequestConfig): Promise<MagicLinkResponse> {
    try {
      const response = await httpPost<MagicLinkResponse>(
        authEndpoints.magicLink,
        magicLinkData,
        toHttpConfig(config)
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_magic_link_error', error, {
          category: 'auth',
          severity: 'error',
          context: { email: magicLinkData.email },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Check if an active UW application exists for the given email
   * GET /api/auth/uw/check-application/:email
   */
  static async checkUwApplication(email: string, config?: RequestConfig): Promise<CheckUwApplicationResponse> {
    try {
      const response = await httpGet<ApiResponse<CheckUwApplicationResponse>>(
        authEndpoints.checkUwApplication(email),
        toHttpConfig(config)
      );
      const payload = response.data as any;
      if (payload?.success && payload?.data) {
        return payload.data;
      }
      return payload;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_check_uw_application_error', error, {
          category: 'auth',
          severity: 'warning',
          context: { email },
          skipSanitization: false,
          user: {}
        });
      }
      return { exists: false, applicationId: null, applicationReference: null, status: null };
    }
  }

  /**
   * Verify a magic link token — GET /api/auth/magic-link/verify/:token
   * Returns access + refresh tokens and user object on success.
   */
  static async verifyMagicLink(token: string, config?: RequestConfig): Promise<LoginResponse> {
    try {
      const response = await httpGet<ApiResponse<LoginResponse>>(
        authEndpoints.magicLinkVerify(token),
        toHttpConfig(config)
      );

      const payload = response.data as any;
      // Handle both envelope { success, data } and raw shapes
      const data = payload?.data ?? payload;

      if (data?.token) {
        localStorage.setItem('authToken', data.token);
      }
      if (data?.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }

      return data as LoginResponse;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_magic_link_verify_error', error, {
          category: 'auth',
          severity: 'error',
          context: { token },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Verify user with token
   */
  static async verifyUser(verifyData: VerifyUserRequest, config?: RequestConfig): ServiceResponse<VerifyUserResponse> {
    try {
      const response = await httpPost<ApiResponse<VerifyUserResponse>>(
        authEndpoints.verifyUser,
        verifyData,
        toHttpConfig(config)
      );

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_verify_error', error, {
          category: 'auth',
          severity: 'error',
          context: { token: verifyData.token },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Get Google OAuth authorization URL
   */
  static async getGoogleAuthUrl(config?: RequestConfig): ServiceResponse<GoogleAuthUrlResponse> {
    try {
      const response = await httpGet<ApiResponse<GoogleAuthUrlResponse> | GoogleAuthUrlResponse>(
        oauthEndpoints.googleAuthUrl,
        toHttpConfig(config)
      );

      // Some environments may return the payload directly without ApiResponse wrapper
      const payload = response.data as any;
      if (payload && typeof payload === 'object' && 'url' in payload && 'state' in payload && !('success' in payload)) {
        return {
          success: true,
          data: payload as GoogleAuthUrlResponse,
          message: 'OK',
          timestamp: new Date().toISOString()
        };
      }

      return response.data as ApiResponse<GoogleAuthUrlResponse>;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('oauth_google_authurl_error', error, {
          category: 'oauth',
          severity: 'error',
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Exchange Google OAuth code for tokens
   */
  static async exchangeGoogleCode(exchangeData: GoogleExchangeRequest, config?: RequestConfig): ServiceResponse<LoginResponse> {
    try {
      const response = await httpPost<GoogleExchangeResponse>(
        oauthEndpoints.googleExchange,
        exchangeData,
        toHttpConfig(config)
      );

      // The backend returns the Google data directly in response.data
      const googleData = response.data;
      
      // Store tokens
      localStorage.setItem('authToken', googleData.access_token);
      if (googleData.refresh_token) {
        localStorage.setItem('refreshToken', googleData.refresh_token);
      }

      // Transform to expected LoginResponse format
      const loginResponse: LoginResponse = {
        user: {
          id: googleData.userId,
          email: googleData.emailAddress,
          name: googleData.displayName,
          roles: [], // Will be populated from profile if needed
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        token: googleData.access_token,
        refreshToken: googleData.refresh_token,
        expiresAt: new Date(Date.now() + 3600000).toISOString(), // 1 hour from now
      };

      return {
        success: true,
        data: loginResponse,
        message: 'Google authentication successful',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('oauth_google_exchange_error', error, {
          category: 'oauth',
          severity: 'error',
          context: { code: exchangeData.code },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Create user account after email verification
   */
  static async createUser(userData: CreateUserFormRequest, config?: RequestConfig): ServiceResponse<CreateUserResponse> {
    try {
      const response = await httpPost<ApiResponse<CreateUserResponse>>(
        authEndpoints.createUser,
        userData,
        toHttpConfig(config)
      );

      // Store token if user creation successful
      if (response.data.success && response.data.data?.token) {
        localStorage.setItem('authToken', response.data.data.token);
        if (response.data.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.data.refreshToken);
        }
      }

      return response.data;
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_create_user_error', error, {
          category: 'auth',
          severity: 'error',
          context: { email: userData.email },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }

  /**
   * Sign in user with magic link token
   */
  static async signInWithToken(tokenData: SignInWithTokenRequest, config?: RequestConfig): Promise<SignInWithTokenResponse> {
    try {
      const response = await httpPost<ApiResponse<SignInWithTokenResponse>>(
        authEndpoints.signin,
        tokenData,
        toHttpConfig(config)
      );

      const responseStatus = response.status;
      const responseData = response.data as unknown as SignInWithTokenApiPayload;

      // Store token if login successful
      if (responseStatus === 200 && responseData) {
        localStorage.setItem('authToken', responseData.access_token);
        if (responseData.refresh_token) {
          localStorage.setItem('refreshToken', responseData.refresh_token);
        }
      }

      return {
        access_token: responseData.access_token,
        refresh_token: responseData.refresh_token,
        user: {
          displayName: responseData.displayName,
          emailAddress: responseData.emailAddress,
          forceChangePassword: responseData.forceChangePassword,
          twoStepVerificationRequired: Boolean(
            responseData.isTwoStepVerificationRequired ?? responseData.twoStepVerificationRequired
          ),
          userId: responseData.userId,
          userName: responseData.userName,
          ...(responseData.isPartner !== undefined ? { isPartner: responseData.isPartner } : {}),
        }
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('auth_signin_token_error', error, {
          category: 'auth',
          severity: 'error',
          context: { token: tokenData.token },
          skipSanitization: false,
          user: {}
        });
      }
      throw error;
    }
  }
}

export default AuthService;
